import { ReactNode, useEffect, useMemo, useState } from 'react';
import { HubCard } from '../hub-card/hub-card.component';
import { HubCardHeaderProps } from '../hub-card-header/hub-card-header.component';
import { TowerRenderer } from '../tower-renderer/tower-renderer.component';
import { TowerRendererProps } from '../tower-renderer/tower-renderer.types';
import { TowerRenderData } from '../tower-renderer/tower-renderer.types';
import { SVGIconComponent } from '../../icons/types';
import { ScreenOrientation } from '../kiosk-tile-content/kiosk-tile-content.component';

export enum HubCardWithTowersModeType {
  Preview = 'preview',
  Interactive = 'interactive'
}

export type HubCardWithTowersMode =
  | { type: HubCardWithTowersModeType.Preview }
  | {
      type: HubCardWithTowersModeType.Interactive;
      hideTooltip?: boolean;
      renderTooltipContent?: TowerRendererProps['renderTooltipContent'];
      renderLockerContent?: TowerRendererProps['renderLockerContent'];
      makeLinkUrl?: TowerRendererProps['makeLinkUrl'];
      makeLinkRequiresPermissions?: TowerRendererProps['makeLinkRequiresPermissions'];
    };

export enum TransactionTypeHoverConfigMode {
  Highlight = 'highlight',
  Outline = 'outline'
}

export type TransactionTypeHoverConfig = {
  mode: TransactionTypeHoverConfigMode;
  accepts: (lockerId: string, type: string) => boolean;
  color?: string;
};

export interface HubCardWithTowersProps {
  headerProps: HubCardHeaderProps;
  towers: TowerRenderData[];
  maxModuleHeight: number;
  isDoubleSided: boolean;
  sideSelectOptions: string[];
  onSelectSide: (label: string) => void;
  selectedSide?: string;
  mode: HubCardWithTowersMode;
  transactionTypes?: { value: string; icon: SVGIconComponent }[];
  onTransactionTypeHover?: (type: string | null) => void;
  transactionTypeHover?: TransactionTypeHoverConfig;
  transactionTypesRightSlot?: ReactNode;
  kioskScreenOrientation?: ScreenOrientation;
  hideHeader?: boolean;
  hideCardFrame?: boolean;
  onTowerTitleClick?: (towerId: string) => void;
}

export function HubCardWithTowers({
  headerProps,
  towers,
  maxModuleHeight,
  isDoubleSided,
  sideSelectOptions,
  onSelectSide,
  selectedSide: externalSelectedSide,
  mode,
  transactionTypes,
  onTransactionTypeHover,
  transactionTypeHover,
  transactionTypesRightSlot,
  kioskScreenOrientation,
  hideHeader,
  hideCardFrame,
  onTowerTitleClick
}: HubCardWithTowersProps) {
  const [internalSelectedSide, setInternalSelectedSide] = useState(sideSelectOptions[0] ?? '');
  const [hoveredTransactionType, setHoveredTransactionType] = useState<string | null>(null);

  const selectedSide = externalSelectedSide ?? internalSelectedSide;
  const handleSelectSide = (side: string) => {
    setInternalSelectedSide(side);
    onSelectSide(side);
  };

  const handleTransactionTypeHover = (type: string | null) => {
    setHoveredTransactionType(type);
    onTransactionTypeHover?.(type);
  };

  useEffect(() => {
    if (!isDoubleSided && sideSelectOptions[0]) {
      setInternalSelectedSide(sideSelectOptions[0]);
    }
  }, [isDoubleSided, sideSelectOptions]);

  const isInverted = isDoubleSided && sideSelectOptions.length >= 2 && selectedSide === sideSelectOptions[1];

  const visibleTowers = useMemo(() => {
    const sideFiltered = isInverted ? towers.map((t) => (t.isDoubleSided ? t : { ...t, lockers: [] })) : towers;

    if (!transactionTypeHover || !hoveredTransactionType) return sideFiltered;

    const { mode: hoverMode, accepts, color } = transactionTypeHover;
    return sideFiltered.map((t) => ({
      ...t,
      lockers: t.lockers.map((locker) => {
        if (locker.isKiosk) return locker;
        if (!accepts(locker.id, hoveredTransactionType)) return locker;
        if (hoverMode === TransactionTypeHoverConfigMode.Outline) {
          return { ...locker, isHighlighted: true, highlightColor: color ?? locker.highlightColor };
        }
        return { ...locker, backgroundColor: color ?? locker.highlightColor ?? locker.backgroundColor };
      })
    }));
  }, [towers, isInverted, transactionTypeHover, hoveredTransactionType]);

  const interactive = mode.type === HubCardWithTowersModeType.Interactive;

  return (
    <HubCard
      headerProps={headerProps}
      isDoubleSided={isDoubleSided}
      onSelectSide={handleSelectSide}
      selectedSide={isDoubleSided ? selectedSide : undefined}
      sideSelectOptions={sideSelectOptions}
      transactionTypes={transactionTypes}
      onTransactionTypeHover={handleTransactionTypeHover}
      transactionTypesRightSlot={transactionTypesRightSlot}
      hideHeader={hideHeader}
      hideCardFrame={hideCardFrame}
    >
      <TowerRenderer
        towers={visibleTowers}
        maxModuleHeight={maxModuleHeight}
        isInverted={isInverted}
        interactive={interactive}
        hideTooltip={mode.type === HubCardWithTowersModeType.Interactive ? mode.hideTooltip : true}
        renderTooltipContent={
          mode.type === HubCardWithTowersModeType.Interactive ? mode.renderTooltipContent : undefined
        }
        renderLockerContent={mode.type === HubCardWithTowersModeType.Interactive ? mode.renderLockerContent : undefined}
        makeLinkUrl={mode.type === HubCardWithTowersModeType.Interactive ? mode.makeLinkUrl : undefined}
        makeLinkRequiresPermissions={
          mode.type === HubCardWithTowersModeType.Interactive ? mode.makeLinkRequiresPermissions : undefined
        }
        kioskScreenOrientation={kioskScreenOrientation}
        onTowerTitleClick={onTowerTitleClick}
      />
    </HubCard>
  );
}
